import { IViewBox } from "@/libs/graphics/types/Graphics.t";

/**
 * Given the viewbox of the target component and the viewbox of the UGC pattern, return the viewbox of
 * the UGC pattern that matches the aspect ratio of the target component and centers the UGC.
 * The goal is to achieve the same effect as a background-size: cover; would.
 * @param {IViewBox} targetComponentViewbox - The viewbox of the target component.
 * @param {IViewBox} ugcViewBox - The viewbox of the UGC pattern
 * @returns The viewbox of the UGC pattern.
 */
export const getUGCViewBoxAsCover = (
  targetComponentViewbox: IViewBox,
  ugcViewBox: IViewBox
): IViewBox => {
  // 1. Set the viewbox of the UGC pattern to the same width and height of the image
  const patternViewBox: IViewBox = {
    x: 0,
    y: 0,
    width: ugcViewBox.width,
    height: ugcViewBox.height,
  };

  // 2. Get the target element's aspect ratio
  const targetElementAspectRatio =
    targetComponentViewbox.height / targetComponentViewbox.width;

  // 3. Get the UGC aspect ratio
  const ugcAspectRatio = ugcViewBox.height / ugcViewBox.width;

  // 4. Adjust pattern aspect ratio to match the target's aspect ratio
  let adjustedDimension: keyof IViewBox | boolean = false;
  // Adjust pattern's width
  if (targetElementAspectRatio > ugcAspectRatio) {
    adjustedDimension = "width";
    patternViewBox.width = patternViewBox.height / targetElementAspectRatio;
  }
  // Adjust pattern's height
  else if (targetElementAspectRatio < ugcAspectRatio) {
    adjustedDimension = "height";
    patternViewBox.height = patternViewBox.width * targetElementAspectRatio;
  }

  // 5. Center image if needed
  if (adjustedDimension) {
    const moveBy =
      (ugcViewBox[adjustedDimension] - patternViewBox[adjustedDimension]) / 2;
    const positionDimensionMap: Record<
      keyof Pick<IViewBox, "width" | "height">,
      keyof Partial<IViewBox>
    > = {
      width: "x",
      height: "y",
    };
    patternViewBox[positionDimensionMap[adjustedDimension]] = moveBy;
  }

  return patternViewBox;
};

/**
 * If the pattern is out of bounds, return true.
 * Essentially, all we need is to check the viewbox of the pattern against the image width and height
 * @param {IViewBox} patternViewBox - IViewBox
 * @param imgDimensions - { width: number; height: number }
 */
export const checkImageOutOfBounds = (
  patternViewBox: IViewBox,
  imgDimensions: { width: number; height: number }
) => {
  return (
    // left
    patternViewBox.x < 0 ||
    // right
    patternViewBox.x > imgDimensions.width - patternViewBox.width ||
    // top
    patternViewBox.y < 0 ||
    // bottom
    patternViewBox.y > imgDimensions.height - patternViewBox.height
  );
};
